import { createContext, ReactNode, useContext, useEffect, useState, useCallback } from 'react'
import { ApolloError, ApolloQueryResult, useApolloClient } from '@apollo/client'
import { ServerError } from '@apollo/client/link/utils'
import { cache } from 'config/apollo/client'
import { analytics } from 'config/segment'
import { CurrentUser } from 'customTypes'
import { useSetFeatureContext } from 'utils/feature'
import {
  useCurrentUserQuery,
  useSignOutUserMutation,
  useSignInPasswordlessUserMutation,
  useSignInUhcMemberMutation,
  SignInPasswordlessUserMutation,
  CurrentUserDocument,
  CurrentUserQuery,
  useSigninUserMutation,
  useSignInAndLinkUhcUserMutation
} from 'types'
import { showSnack } from './ui'

export interface LoginData {
  email: string
  password: string
}

export interface UserAuthResponse
  extends Pick<
    SignInPasswordlessUserMutation['signInPasswordlessUser'],
    'success' | 'token' | 'user' | 'role'
  > {
  status?: 'ok' | 'invalid_token' | 'guest' | 'exists' | 'error'
}

export const NOT_INITIALIZED = 'Waiting for auth provider'
export const INVALID_CREDENTIALS = 'Invalid credentials'
export const LOGIN_FAILED = 'An unknown error occurred, please try again'

interface AuthContextValues {
  loading: boolean
  isAuthorized: boolean
  isInitializing: boolean
  isSigningIn: boolean
  currentUser: CurrentUser
  updateCurrentUser: (user: CurrentUser) => Promise<void>
  passwordlessSignIn: (signInToken: string) => Promise<UserAuthResponse | void>
  signInUhcMember: (token: string) => Promise<UserAuthResponse | void>
  emailSignIn: (email: string, password: string) => Promise<UserAuthResponse | void>
  signInAndLinkUhcUser: (email: string, password: string) => Promise<UserAuthResponse | void>
  signOut: () => Promise<void>
  refetchCurrentUser: (
    variables?: Partial<Record<string, never>>
  ) => Promise<ApolloQueryResult<CurrentUserQuery>>
}

const defaultValues: AuthContextValues = {
  loading: true,
  isAuthorized: false,
  isInitializing: true,
  isSigningIn: false,
  currentUser: null,
  updateCurrentUser: async (_: CurrentUser) => {},
  passwordlessSignIn: async (_: string): Promise<UserAuthResponse | void> => {
    throw NOT_INITIALIZED
  },
  signInUhcMember: async (_: string): Promise<UserAuthResponse | void> => {
    throw NOT_INITIALIZED
  },
  emailSignIn: async (_email: string, _password: string): Promise<UserAuthResponse | void> => {
    throw NOT_INITIALIZED
  },
  signInAndLinkUhcUser: async (
    _email: string,
    _password: string
  ): Promise<UserAuthResponse | void> => {
    throw NOT_INITIALIZED
  },
  signOut: async (): Promise<void> => {},
  refetchCurrentUser: async (
    _?: Partial<Record<string, never>>
  ): Promise<ApolloQueryResult<CurrentUserQuery>> => {
    throw NOT_INITIALIZED
  }
}

const signInMutationConfig = {
  fetchPolicy: 'network-only',
  onError: (e: ApolloError) => {
    if (e.networkError && (e.networkError as ServerError).statusCode === 401) {
      throw INVALID_CREDENTIALS
    }
    throw e
  }
} as const

const AuthContext = createContext(defaultValues)
export const useAuth = () => useContext(AuthContext)

export enum UserRoles {
  Guest = 'guest',
  User = 'user',
  Admin = 'admin'
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const client = useApolloClient()
  const {
    data: { currentUser = null } = {},
    loading: isInitializing,
    refetch: refetchCurrentUser
  } = useCurrentUserQuery()
  const setFeatureContext = useSetFeatureContext()
  const [loggedIn, setLoggedIn] = useState(false)
  const [isSigningIn, setSigningIn] = useState(false)

  const updateCurrentUser = useCallback(
    async (user: NonNullable<CurrentUser>) => {
      client.writeQuery({
        query: CurrentUserDocument,
        data: { currentUser: user }
      })
    },
    [client]
  )

  const [passwordlessSignInMutation] = useSignInPasswordlessUserMutation(signInMutationConfig)
  const [signInUhcMemberMutation] = useSignInUhcMemberMutation(signInMutationConfig)
  const [emailSignInMutation] = useSigninUserMutation(signInMutationConfig)
  const [signInAndLinkUhcUserMutation] = useSignInAndLinkUhcUserMutation(signInMutationConfig)

  const [signOutMutation] = useSignOutUserMutation({
    onError: () => {
      showSnack('Unable to sign out. Please try again later.', 'error')
    }
  })

  useEffect(() => {
    setLoggedIn(!!currentUser)
  }, [currentUser])

  const tryLogin = useCallback(
    async (auth: UserAuthResponse | null | undefined): Promise<UserAuthResponse> => {
      if (!auth) {
        throw LOGIN_FAILED
      }

      const { success = false, token: accessToken, user, status } = auth

      if (success && status === 'guest') {
        // Return without setting loggedIn to true but without raising any errors.
        return auth
      }

      if (!accessToken) {
        throw INVALID_CREDENTIALS
      } else if (!success) {
        throw LOGIN_FAILED
      }

      if (user) {
        await setFeatureContext(user)
        await updateCurrentUser(user)
      }

      setLoggedIn(true)
      return auth
    },
    [setLoggedIn, setFeatureContext, updateCurrentUser]
  )

  const passwordlessSignIn = useCallback(
    async (signInToken: string): Promise<UserAuthResponse | void> => {
      setSigningIn(true)
      try {
        const { data: response } = await passwordlessSignInMutation({
          variables: { signInToken }
        })

        return await tryLogin(response?.signInPasswordlessUser)
      } finally {
        setSigningIn(false)
      }
    },
    [setSigningIn, tryLogin, passwordlessSignInMutation]
  )

  const signInUhcMember = useCallback(
    async (token: string): Promise<UserAuthResponse | void> => {
      setSigningIn(true)
      try {
        const { data: response } = await signInUhcMemberMutation({
          variables: { token }
        })

        return await tryLogin(response?.signInUhcMember as UserAuthResponse)
      } finally {
        setSigningIn(false)
      }
    },
    [setSigningIn, tryLogin, signInUhcMemberMutation]
  )

  const emailSignIn = useCallback(
    async (email: string, password: string): Promise<UserAuthResponse | void> => {
      setSigningIn(true)
      try {
        const { data: response } = await emailSignInMutation({
          variables: { email, password }
        })

        return await tryLogin(response?.signinUser as UserAuthResponse)
      } finally {
        setSigningIn(false)
      }
    },
    [setSigningIn, tryLogin, emailSignInMutation]
  )

  const signInAndLinkUhcUser = useCallback(
    async (email: string, password: string): Promise<UserAuthResponse | void> => {
      setSigningIn(true)
      try {
        const { data: response } = await signInAndLinkUhcUserMutation({
          variables: { email, password }
        })

        return await tryLogin(response?.signInAndLinkUhcUser as UserAuthResponse)
      } finally {
        setSigningIn(false)
      }
    },
    [setSigningIn, tryLogin, signInAndLinkUhcUserMutation]
  )

  const signOut = useCallback(async () => {
    await signOutMutation()
    analytics.reset()
    localStorage.clear()
    sessionStorage.clear()
    setLoggedIn(false)

    if (currentUser?.id) {
      const normalizedId = cache.identify({ id: currentUser.id, __typename: 'User' })
      cache.evict({ id: normalizedId })
      cache.gc()
    }
  }, [signOutMutation, currentUser?.id])

  const value: AuthContextValues = {
    loading: isInitializing || isSigningIn,
    isAuthorized: loggedIn || !!currentUser,
    isInitializing,
    isSigningIn,
    currentUser,
    updateCurrentUser,
    passwordlessSignIn,
    signInUhcMember,
    emailSignIn,
    signInAndLinkUhcUser,
    signOut,
    refetchCurrentUser
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
